import { useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { MessageCircle } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Toaster } from "@/components/ui/sonner";
import { waLink, URGENT_SERVICES, NURSING_SERVICES, LOCATIONS } from "@/data/site";

const ALL_SERVICES = [...URGENT_SERVICES, ...NURSING_SERVICES];

/** Build a pre-filled WhatsApp message based on the current route. */
const buildWaMessage = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  // strip locale prefix (en, vi, de, fr, ru) if present
  const segs = parts[0]?.length === 2 ? parts.slice(1) : parts;
  const [top, slug] = segs;

  const svc = slug ? ALL_SERVICES.find((s) => s.slug === slug) : null;
  const loc = top === "locations" && slug ? LOCATIONS.find((l) => l.slug === slug) : null;

  if (svc && loc) return `Hello Doctors Vietnam, I need help with ${svc.title} in ${loc.name}.`;
  if (svc) return `Hello Doctors Vietnam, I need help with ${svc.title}.`;
  if (loc) return `Hello Doctors Vietnam, I need help finding a doctor in ${loc.name}.`;
  if (top === "urgent-care") return "Hello Doctors Vietnam, I need urgent (non-emergency) medical support.";
  if (top === "nursing") return "Hello Doctors Vietnam, I need nursing or home care support.";
  if (top === "common-health-problems-vietnam") return "Hello Doctors Vietnam, I'm feeling unwell and need help finding a doctor.";
  if (top === "medical-tourism") return "Hello Doctors Vietnam, I'm planning treatment in Vietnam and would like guidance.";
  return "Hello Doctors Vietnam, I need help finding a doctor in Vietnam.";
};

export const Layout = () => {
  const { pathname } = useLocation();
  const waMessage = useMemo(() => buildWaMessage(pathname), [pathname]);

  // Smooth momentum scrolling (respects reduced-motion by disabling below)
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />

      <a
        href={waLink(waMessage)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 z-40 grid h-14 w-14 place-items-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform duration-300 hover:scale-105"
      >
        <MessageCircle className="h-7 w-7" />
      </a>

      <Toaster position="top-center" richColors />
    </div>
  );
};

export default Layout;
